// Home state center coordinates and zoom levels for map initialization

interface HomeStateCoordinates {
  center: [number, number]; // [latitude, longitude]
  zoom: number;
}

const HOME_STATE_COORDINATES: Record<string, HomeStateCoordinates> = {
  'CO': { center: [39.0, -105.5], zoom: 6.5 }, // Colorado
  'NM': { center: [34.0, -106.0], zoom: 6.5 }, // New Mexico
  'WA': { center: [47.4, -120.5], zoom: 6.5 }, // Washington
  'OR': { center: [44.0, -120.5], zoom: 6.5 }, // Oregon
  'CA': { center: [36.8, -119.4], zoom: 6.0 }, // California
  'MT': { center: [46.8, -110.4], zoom: 6.5 }, // Montana
  'ID': { center: [44.4, -114.4], zoom: 6.5 }, // Idaho
  'WY': { center: [42.8, -107.2], zoom: 6.5 }, // Wyoming
  'UT': { center: [39.3, -111.6], zoom: 6.5 }, // Utah
  'AZ': { center: [33.7, -111.6], zoom: 6.5 }, // Arizona
  'NV': { center: [38.8, -116.4], zoom: 6.5 }, // Nevada
  'TX': { center: [31.5, -100.0], zoom: 6.0 }, // Texas
  'OK': { center: [35.5, -97.5], zoom: 6.5 }, // Oklahoma
  'KS': { center: [38.5, -98.0], zoom: 6.5 }, // Kansas
  'NE': { center: [41.5, -99.0], zoom: 6.5 }, // Nebraska
  'SD': { center: [44.5, -100.0], zoom: 6.5 }, // South Dakota
  'ND': { center: [47.5, -100.0], zoom: 6.5 }, // North Dakota
  'MN': { center: [46.0, -94.0], zoom: 6.0 }, // Minnesota
  'IA': { center: [42.0, -93.5], zoom: 6.5 }, // Iowa
  'MO': { center: [38.5, -92.5], zoom: 6.5 }, // Missouri
  'AR': { center: [34.8, -92.2], zoom: 6.5 }, // Arkansas
  'LA': { center: [31.0, -91.5], zoom: 6.5 }, // Louisiana
  'MS': { center: [32.7, -89.7], zoom: 6.5 }, // Mississippi
  'AL': { center: [32.8, -86.8], zoom: 6.5 }, // Alabama
  'GA': { center: [32.6, -83.5], zoom: 6.5 }, // Georgia
  'FL': { center: [27.8, -81.8], zoom: 6.0 }, // Florida
  'SC': { center: [33.9, -80.9], zoom: 6.5 }, // South Carolina
  'NC': { center: [35.6, -79.8], zoom: 6.5 }, // North Carolina
  'TN': { center: [35.7, -86.4], zoom: 6.5 }, // Tennessee
  'KY': { center: [37.7, -85.9], zoom: 6.5 }, // Kentucky
  'VA': { center: [37.5, -78.5], zoom: 6.5 }, // Virginia
  'WV': { center: [38.6, -80.6], zoom: 6.5 }, // West Virginia
  'OH': { center: [40.4, -82.6], zoom: 6.5 }, // Ohio
  'IN': { center: [39.8, -86.1], zoom: 6.5 }, // Indiana
  'IL': { center: [39.8, -89.0], zoom: 6.5 }, // Illinois
  'WI': { center: [44.8, -89.6], zoom: 6.5 }, // Wisconsin
  'MI': { center: [44.3, -85.6], zoom: 6.0 }, // Michigan
  'PA': { center: [40.9, -77.8], zoom: 6.5 }, // Pennsylvania
  'NY': { center: [42.7, -74.2], zoom: 6.0 }, // New York
  'ME': { center: [45.3, -69.0], zoom: 6.5 }, // Maine
  'NH': { center: [44.0, -71.5], zoom: 6.5 }, // New Hampshire
  'VT': { center: [43.9, -72.4], zoom: 6.5 }, // Vermont
  'MA': { center: [42.0, -71.8], zoom: 6.5 }, // Massachusetts
  'RI': { center: [41.6, -71.5], zoom: 6.5 }, // Rhode Island
  'CT': { center: [41.5, -72.7], zoom: 6.5 }, // Connecticut
  'NJ': { center: [40.2, -74.5], zoom: 6.5 }, // New Jersey
  'DE': { center: [39.1, -75.4], zoom: 6.5 }, // Delaware
  'MD': { center: [38.8, -77.2], zoom: 6.5 }, // Maryland
};

// Default coordinates (center of US) for when no home state is set
export const DEFAULT_COORDINATES: HomeStateCoordinates = {
  center: [39.8283, -98.5795], // Center of US
  zoom: 4.5
};

// Get coordinates for a specific home state
export const getHomeStateCoordinates = (stateAbbr: string): HomeStateCoordinates => {
  return HOME_STATE_COORDINATES[stateAbbr] || DEFAULT_COORDINATES;
};

const CAMERA_LAT_LNG_EPS = 0.05;
const CAMERA_ZOOM_EPS = 0.15;

/** True when the camera is already at the home-state seed (skip a redundant first flyTo). */
export function camerasMatchHomeState(
  view: { latitude?: number; longitude?: number; zoom?: number } | null | undefined,
  stateAbbr: string,
): boolean {
  if (
    view == null ||
    typeof view.latitude !== 'number' ||
    typeof view.longitude !== 'number' ||
    typeof view.zoom !== 'number'
  ) {
    return false;
  }
  const home = getHomeStateCoordinates(stateAbbr);
  return (
    Math.abs(view.latitude - home.center[0]) <= CAMERA_LAT_LNG_EPS &&
    Math.abs(view.longitude - home.center[1]) <= CAMERA_LAT_LNG_EPS &&
    Math.abs(view.zoom - home.zoom) <= CAMERA_ZOOM_EPS
  );
}
